'use client';

import { useEffect } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { CartActions } from './cart-actions.component';
import { CartDrawerHeader } from './cart-drawer-header.component';
import { CartEmptyState } from './cart-empty-state.component';
import { CartFooter } from './cart-footer.component';
import { CartItemsList } from './cart-items-list.component';

import { SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton.component';
import { useToast } from '@/components/ui/use-toast';
import { useCart } from '@/hooks/use-cart.hook';
import { retryCart } from '@/store/cart/cart.store';

export const CART_LOAD_ERROR_MESSAGE = 'Erro ao carregar seu carrinho.';
export const CART_LOAD_RETRY_LABEL = 'Tentar novamente';

interface CartDrawerProps {
  onAddProcedure: () => void;
  onSearch: () => void;
}

export function CartDrawer({ onAddProcedure, onSearch }: CartDrawerProps) {
  const { items, itemCount, total, isLoading, hasError, isEmpty, hasItems } = useCart();
  const { toast } = useToast();

  useEffect(() => {
    if (!hasError) {
      return;
    }

    toast.error(CART_LOAD_ERROR_MESSAGE, {
      action: { text: CART_LOAD_RETRY_LABEL, onClick: () => void retryCart() },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  const showsEmptyState = isEmpty || hasError;

  return (
    <SheetContent
      side="right"
      hasHeader
      title="Carrinho"
      className="w-full gap-0 p-0 md:w-[22.5rem]"
      onOpenAutoFocus={(event) => event.preventDefault()}
      onInteractOutside={(event) => {
        if ((event.target as Element | null)?.closest('[data-toast-viewport]')) {
          event.preventDefault();
        }
      }}
      data-testid={DataTestIdEnum.CART_DRAWER}
    >
      <CartDrawerHeader itemCount={itemCount} />

      {hasItems && <CartActions onAddProcedure={onAddProcedure} />}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex flex-col gap-4 px-6 pb-5" data-testid={DataTestIdEnum.CART_LOADING}>
            <Skeleton className="h-40 w-full rounded-xxl" />
            <Skeleton className="h-40 w-full rounded-xxl" />
          </div>
        )}

        {showsEmptyState && <CartEmptyState onSearch={onSearch} />}

        {hasItems && <CartItemsList items={items} />}
      </div>

      {hasItems && <CartFooter total={total} />}
    </SheetContent>
  );
}
